import React, { useEffect, useRef, useState } from "react";
import Pool from "../../UserPool";
import { customFetch, getLoggedInUserRole } from "../../utils/Auth";
import { trackExternalEvent } from "../../utils/logging";
import ChartHistoryTable from "../Support/ChartHistoryTable";
import { formatChartHistoryFieldLabel } from "../Support/chartHistoryUtils";
import VAEvalCaseQuery from "./VAEvalCaseQuery";
import LiveTranscriptPanel from "./LiveTranscriptPanel";
import CodingPanel from "./CodingPanel";
import { environment } from "../../config/environment";

const EMPTY_AUDIO = { title: "", url: "" };
const REVIEWED_CASES_STORAGE_KEY = "va-eval-reviewed-cases";
const VIEW_STORAGE_KEY = "va-eval-view";
const LIVE_TRANSCRIPT_AUTOSAVE_DELAY_MS = 5000;
const KNOWTEX_ORG = "Knowtex";
const EVAL_ROLES = ["documentation", "coding"];
const KNOWTEX_VIEW_OPTIONS = [
  { id: "all", label: "All" },
  { id: "documentation", label: "Documentation" },
  { id: "coding", label: "Coding" },
];
const readStoredKnowtexView = () => {
  try {
    const stored = String(localStorage.getItem(VIEW_STORAGE_KEY) || "");
    return KNOWTEX_VIEW_OPTIONS.some((option) => option.id === stored)
      ? stored
      : "all";
  } catch (_error) {
    return "all";
  }
};
const sanitizeReviewedCaseMap = (rawMap) => {
  if (!rawMap || typeof rawMap !== "object" || Array.isArray(rawMap)) {
    return {};
  }

  return Object.entries(rawMap).reduce((sanitizedMap, [patientId, reviewed]) => {
    const normalizedPatientId = String(patientId || "").trim();
    if (!normalizedPatientId || reviewed !== true) {
      return sanitizedMap;
    }

    sanitizedMap[normalizedPatientId] = true;
    return sanitizedMap;
  }, {});
};

export default function VAEvalLanding({
  setAuth,
  setUserOrg,
  userOrg,
}) {
  const isKnowtexUser = userOrg === KNOWTEX_ORG;
  const [knowtexView, setKnowtexView] = useState(readStoredKnowtexView);
  const [reviewerRole, setReviewerRole] = useState(undefined);
  const [codingData, setCodingData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentAudio, setCurrentAudio] = useState(EMPTY_AUDIO);
  const [activeCasePatientId, setActiveCasePatientId] = useState("");
  const [activeEvalCase, setActiveEvalCase] = useState(null);
  const [caseJobs, setCaseJobs] = useState([]);
  const [activeJobIndex, setActiveJobIndex] = useState(0);
  const [recordingDateTimeLabel, setRecordingDateTimeLabel] = useState("");
  const [transcriptText, setTranscriptText] = useState("");
  const [aiNoteText, setAiNoteText] = useState("");
  const [liveTranscriptText, setLiveTranscriptText] = useState("");
  const [prechartHistory, setPrechartHistory] = useState(null);
  const [duration, setDuration] = useState("");
  const [currTime, setCurrTime] = useState("");
  const [playbackRate, setPlaybackRate] = useState(1);
  const [reviewedCaseMap, setReviewedCaseMap] = useState({});
  const [copiedField, setCopiedField] = useState("");

  const audioElem = useRef();
  const copyResetTimeoutRef = useRef(null);
  const liveTranscriptAutosaveTimeoutRef = useRef(null);
  const liveTranscriptLastSavedByPatientRef = useRef({});
  const liveTranscriptFirstSaveLoggedByPatientRef = useRef({});
  const chapiDataScrollRef = useRef(null);
  const hasAudio = Boolean(currentAudio.url);
  const activeView = isKnowtexUser ? knowtexView : reviewerRole;
  const isViewResolved = isKnowtexUser || reviewerRole !== undefined;
  const hasValidView = isKnowtexUser || EVAL_ROLES.includes(reviewerRole);
  const showDocumentation =
    activeView === "all" || activeView === "documentation";
  const showCoding = activeView === "all" || activeView === "coding";
  const canRecordResults = !isKnowtexUser && showDocumentation;
  const hasPrechartHistory = Boolean(
    prechartHistory &&
      typeof prechartHistory === "object" &&
      Object.keys(prechartHistory).length > 0,
  );

  const formatTime = (seconds) => {
    const safeSeconds = Number.isFinite(seconds) ? seconds : 0;
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = Math.floor(safeSeconds % 60);
    return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
  };

  const clearLoadedJob = () => {
    setCurrentAudio(EMPTY_AUDIO);
    setActiveEvalCase(null);
    setCaseJobs([]);
    setActiveJobIndex(0);
    setRecordingDateTimeLabel("");
    setTranscriptText("");
    setAiNoteText("");
    setLiveTranscriptText("");
    setPrechartHistory(null);
    setCodingData(null);
    setDuration("");
    setCurrTime("");
    setPlaybackRate(1);
    setIsPlaying(false);
    if (liveTranscriptAutosaveTimeoutRef.current) {
      clearTimeout(liveTranscriptAutosaveTimeoutRef.current);
      liveTranscriptAutosaveTimeoutRef.current = null;
    }
    if (audioElem.current) {
      audioElem.current.currentTime = 0;
    }
  };

  const applyPlaybackJob = (evalCase, jobDetail) => {
    const appointmentLabel = evalCase.appointment_id
      ? `Appointment ${evalCase.appointment_id}`
      : "Appointment -";
    const patientLabel = `Patient ${String(evalCase.patient_id || "-")}`;

    setCurrentAudio({
      title: `${appointmentLabel} | ${patientLabel}`,
      url: jobDetail.audio_url || "",
    });
    const nextRecordingDateTimeLabel = String(
      jobDetail?.recording_datetime_label ||
        evalCase?.recording_datetime_label ||
        "",
    ).trim();
    setRecordingDateTimeLabel(nextRecordingDateTimeLabel);
    setTranscriptText(jobDetail.transcript_text || "");
    setAiNoteText(jobDetail.ai_note || "");
    setDuration("");
    setCurrTime("");
    setPlaybackRate(1);
    setIsPlaying(false);

    if (audioElem.current) {
      audioElem.current.playbackRate = 1;
      audioElem.current.currentTime = 0;
    }
  };

  const applyEvalCaseDetail = (evalCase, caseDetail) => {
    if (!evalCase || !caseDetail) {
      clearLoadedJob();
      return;
    }

    const nextJobs =
      Array.isArray(caseDetail.jobs) && caseDetail.jobs.length > 0
        ? caseDetail.jobs
        : [caseDetail];
    setActiveEvalCase(evalCase);
    setCaseJobs(nextJobs);
    setActiveJobIndex(0);
    setPrechartHistory(
      caseDetail.prechart_history &&
        typeof caseDetail.prechart_history === "object" &&
        !Array.isArray(caseDetail.prechart_history)
        ? caseDetail.prechart_history
        : null,
    );
    setCodingData(
      caseDetail.coding &&
        typeof caseDetail.coding === "object" &&
        !Array.isArray(caseDetail.coding)
        ? caseDetail.coding
        : null,
    );
    applyPlaybackJob(evalCase, nextJobs[0]);
    const normalizedPatientId = String(evalCase.patient_id || "").trim();
    setLiveTranscriptText("");
    if (normalizedPatientId) {
      liveTranscriptLastSavedByPatientRef.current[normalizedPatientId] = "";
      liveTranscriptFirstSaveLoggedByPatientRef.current[normalizedPatientId] =
        false;
    }
    if (liveTranscriptAutosaveTimeoutRef.current) {
      clearTimeout(liveTranscriptAutosaveTimeoutRef.current);
      liveTranscriptAutosaveTimeoutRef.current = null;
    }
  };

  const cyclePlaybackJob = (direction) => {
    if (!activeEvalCase || caseJobs.length <= 1) {
      return;
    }
    const nextIndex =
      (activeJobIndex + direction + caseJobs.length) % caseJobs.length;
    setActiveJobIndex(nextIndex);
    applyPlaybackJob(activeEvalCase, caseJobs[nextIndex]);
    void trackExternalEvent("va_eval:playback_job_changed");
  };

  const scrollToChapiField = (field) => {
    const scrollContainer = chapiDataScrollRef.current;
    if (!scrollContainer) {
      return;
    }
    const fieldSection = Array.from(
      scrollContainer.querySelectorAll("[data-chart-history-field]"),
    ).find(
      (section) => section.getAttribute("data-chart-history-field") === field,
    );
    if (!fieldSection) {
      return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    const sectionRect = fieldSection.getBoundingClientRect();
    scrollContainer.scrollTo({
      top:
        scrollContainer.scrollTop +
        sectionRect.top -
        containerRect.top -
        8,
      behavior: "smooth",
    });
  };

  const togglePlayback = () => {
    if (!hasAudio) {
      return;
    }
    const shouldPlay = !isPlaying;
    if (shouldPlay) {
      markCaseAsReviewed(activeCasePatientId);
      void trackExternalEvent("va_eval:audio_play_started");
    }
    setIsPlaying(shouldPlay);
  };

  const skipBy = (secondsDelta) => {
    if (!audioElem.current || !hasAudio) {
      return;
    }

    const maxDuration = Number.isFinite(audioElem.current.duration)
      ? audioElem.current.duration
      : 0;
    const nextTime = Math.max(
      0,
      Math.min((audioElem.current.currentTime || 0) + secondsDelta, maxDuration),
    );
    audioElem.current.currentTime = nextTime;
    setCurrTime(nextTime);
  };

  const handleSeek = (event) => {
    if (!audioElem.current || !hasAudio) {
      return;
    }
    const nextTime = Number(event.target.value);
    audioElem.current.currentTime = nextTime;
    setCurrTime(nextTime);
  };

  const handlePlaybackRateChange = (event) => {
    const nextRate = Number(event.target.value);
    setPlaybackRate(nextRate);
    if (audioElem.current) {
      audioElem.current.playbackRate = nextRate;
    }
  };

  const persistLiveTranscriptText = async (patientId, nextLiveTranscriptText) => {
    const normalizedPatientId = String(patientId || "").trim();
    if (!normalizedPatientId || !canRecordResults) {
      return;
    }
    const liveTranscriptValue =
      typeof nextLiveTranscriptText === "string"
        ? nextLiveTranscriptText
        : String(nextLiveTranscriptText || "");

    try {
      const previousSavedValue =
        liveTranscriptLastSavedByPatientRef.current[normalizedPatientId] ?? "";
      const response = await customFetch(
        `${environment.apiUrl}/patients/va-eval/cases/${encodeURIComponent(normalizedPatientId)}/live-transcript`,
        {
          method: "POST",
          body: JSON.stringify({ live_transcript_text: liveTranscriptValue }),
        },
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || "Failed to persist live transcript text");
      }
      liveTranscriptLastSavedByPatientRef.current[normalizedPatientId] =
        liveTranscriptValue;
      if (
        !liveTranscriptFirstSaveLoggedByPatientRef.current[normalizedPatientId] &&
        !String(previousSavedValue || "").trim() &&
        String(liveTranscriptValue || "").trim()
      ) {
        liveTranscriptFirstSaveLoggedByPatientRef.current[normalizedPatientId] =
          true;
        void trackExternalEvent("va_eval:live_transcript_autosaved");
      }
    } catch (error) {
      console.error(
        "Failed to persist live transcript text for VA eval case:",
        error,
      );
    }
  };

  const persistLiveTranscriptIfDirty = async (
    patientId,
    nextLiveTranscriptText,
  ) => {
    const normalizedPatientId = String(patientId || "").trim();
    if (!normalizedPatientId) {
      return;
    }

    const liveTranscriptValue =
      typeof nextLiveTranscriptText === "string"
        ? nextLiveTranscriptText
        : String(nextLiveTranscriptText || "");
    const lastSavedValue =
      liveTranscriptLastSavedByPatientRef.current[normalizedPatientId] ?? "";
    if (liveTranscriptValue === lastSavedValue) {
      return;
    }

    await persistLiveTranscriptText(normalizedPatientId, liveTranscriptValue);
  };

  const handleLiveTranscriptTextChange = (event) => {
    const nextValue = String(event.target.value || "");
    setLiveTranscriptText(nextValue);

    const normalizedPatientId = String(activeCasePatientId || "").trim();
    if (!normalizedPatientId) {
      return;
    }

    const lastSavedValue =
      liveTranscriptLastSavedByPatientRef.current[normalizedPatientId] ?? "";

    if (liveTranscriptAutosaveTimeoutRef.current) {
      clearTimeout(liveTranscriptAutosaveTimeoutRef.current);
      liveTranscriptAutosaveTimeoutRef.current = null;
    }

    if (nextValue === lastSavedValue) {
      return;
    }

    liveTranscriptAutosaveTimeoutRef.current = setTimeout(() => {
      void persistLiveTranscriptIfDirty(normalizedPatientId, nextValue);
    }, LIVE_TRANSCRIPT_AUTOSAVE_DELAY_MS);
  };

  const persistCopiedField = async (patientId, copiedItem) => {
    const normalizedPatientId = String(patientId || "").trim();
    if (!normalizedPatientId || !canRecordResults) {
      return;
    }

    const normalizedCopiedItem = String(copiedItem || "").trim().toLowerCase();
    if (
      normalizedCopiedItem !== "transcript" &&
      normalizedCopiedItem !== "note" &&
      normalizedCopiedItem !== "live_transcript"
    ) {
      return;
    }

    try {
      const response = await customFetch(
        `${environment.apiUrl}/patients/va-eval/cases/${encodeURIComponent(normalizedPatientId)}/copy`,
        {
          method: "POST",
          body: JSON.stringify({ copied_item: normalizedCopiedItem }),
        },
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || "Failed to persist copied state");
      }
      void trackExternalEvent(`va_eval:copy_${normalizedCopiedItem}`);
    } catch (error) {
      console.error("Failed to persist copied field for VA eval case:", error);
    }
  };

  const copyTextContent = async (fieldKey, rawText) => {
    const textToCopy = String(rawText || "").trim();
    if (!textToCopy) {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else if (typeof document !== "undefined") {
        const textArea = document.createElement("textarea");
        textArea.value = textToCopy;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      } else {
        return;
      }

      setCopiedField(fieldKey);
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
      copyResetTimeoutRef.current = setTimeout(() => {
        setCopiedField("");
      }, 2500);
      void persistCopiedField(activeCasePatientId, fieldKey);
    } catch (error) {
      console.error("Failed to copy eval text:", error);
    }
  };

  const toggleReviewedCase = (patientId) => {
    const normalizedPatientId = String(patientId || "").trim();
    if (!normalizedPatientId) {
      return;
    }

    setReviewedCaseMap((previousMap) => {
      const nextMap = { ...previousMap };
      if (nextMap[normalizedPatientId]) {
        delete nextMap[normalizedPatientId];
      } else {
        nextMap[normalizedPatientId] = true;
      }
      return nextMap;
    });
    void trackExternalEvent("va_eval:reviewed_toggled");
  };

  const markCaseAsReviewed = (patientId) => {
    const normalizedPatientId = String(patientId || "").trim();
    if (!normalizedPatientId) {
      return;
    }

    setReviewedCaseMap((previousMap) => {
      if (previousMap[normalizedPatientId]) {
        return previousMap;
      }
      return {
        ...previousMap,
        [normalizedPatientId]: true,
      };
    });
  };

  useEffect(() => {
    if (!audioElem.current || !currentAudio.url) {
      return;
    }

    if (isPlaying) {
      const playPromise = audioElem.current.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => setIsPlaying(false));
      }
    } else {
      audioElem.current.pause();
    }
  }, [isPlaying, currentAudio.url]);

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
      if (liveTranscriptAutosaveTimeoutRef.current) {
        clearTimeout(liveTranscriptAutosaveTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isKnowtexUser || userOrg === null || userOrg === undefined) {
      return;
    }
    let cancelled = false;
    getLoggedInUserRole().then((role) => {
      if (!cancelled) {
        setReviewerRole(role);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isKnowtexUser, userOrg]);

  useEffect(() => {
    if (!isKnowtexUser) {
      return;
    }
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, knowtexView);
    } catch (_error) {
      // Storage is a convenience only.
    }
  }, [isKnowtexUser, knowtexView]);

  useEffect(() => {
    try {
      const storedValue = localStorage.getItem(REVIEWED_CASES_STORAGE_KEY);
      if (!storedValue) {
        return;
      }
      const parsed = JSON.parse(storedValue);
      const sanitizedReviewedMap = sanitizeReviewedCaseMap(parsed);
      if (Object.keys(sanitizedReviewedMap).length > 0) {
        setReviewedCaseMap(sanitizedReviewedMap);
      }
    } catch (error) {
      console.error("Failed to restore reviewed cases from storage:", error);
    }
  }, []);

  useEffect(() => {
    const sanitizedReviewedMap = sanitizeReviewedCaseMap(reviewedCaseMap);
    if (Object.keys(sanitizedReviewedMap).length === 0) {
      localStorage.removeItem(REVIEWED_CASES_STORAGE_KEY);
      return;
    }

    localStorage.setItem(
      REVIEWED_CASES_STORAGE_KEY,
      JSON.stringify(sanitizedReviewedMap),
    );
  }, [reviewedCaseMap]);

  const handleCaseLoaded = ({
    patientId,
    appointmentId,
    recordingDateTimeLabel: nextRecordingDateTimeLabel,
    caseDetail,
  }) => {
    void persistLiveTranscriptIfDirty(activeCasePatientId, liveTranscriptText);
    const normalizedPatientId = String(patientId || "").trim();
    setActiveCasePatientId(normalizedPatientId);
    applyEvalCaseDetail(
      {
        patient_id: normalizedPatientId,
        appointment_id: String(appointmentId || "").trim(),
        recording_datetime_label: String(nextRecordingDateTimeLabel || "").trim(),
      },
      caseDetail,
    );
    setErrorMessage("");
  };

  const handleNoCases = () => {
    void persistLiveTranscriptIfDirty(activeCasePatientId, liveTranscriptText);
    setActiveCasePatientId("");
    clearLoadedJob();
  };

  const onPlaying = () => {
    if (!audioElem.current) {
      return;
    }
    const nextDuration = Number.isFinite(audioElem.current.duration)
      ? audioElem.current.duration
      : 0;
    const nextTime = Number.isFinite(audioElem.current.currentTime)
      ? audioElem.current.currentTime
      : 0;
    setDuration(nextDuration);
    setCurrTime(nextTime);
  };

  const logoutUser = () => {
    void persistLiveTranscriptIfDirty(activeCasePatientId, liveTranscriptText);
    const user = Pool.getCurrentUser();
    if (user) {
      user.signOut();
    }

    localStorage.removeItem("token");
    setUserOrg(null);
    setAuth(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 text-ktx-purple px-2 md:px-3 lg:px-4 py-5">
      <button
        className="fixed top-4 right-4 flex hover:text-ktx-navy font-medium text-[24px] items-center gap-1"
        onClick={logoutUser}
        type="button"
      >
        <svg
          className="w-6 h-6"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M20 12H8m12 0-4 4m4-4-4-4M9 4H7a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h2"
          />
        </svg>
        Logout
      </button>

      <div className="w-full max-w-[96vw] mx-auto">
        <div className="mb-4 flex flex-wrap items-center gap-4">
          <h1 className="text-[36px] font-semibold">VA Eval Workspace</h1>
          {isKnowtexUser && (
            <div
              className="flex items-center gap-1 rounded-xl border border-ktx-purple/30 bg-white p-1 shadow-sm"
              role="radiogroup"
              aria-label="Review view"
            >
              {KNOWTEX_VIEW_OPTIONS.map((option) => {
                const isActive = knowtexView === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={isActive}
                    onClick={() => setKnowtexView(option.id)}
                    className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                      isActive
                        ? "bg-ktx-purple text-white"
                        : "text-ktx-navy hover:bg-barely-purple"
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          )}
          {!isKnowtexUser && hasValidView && (
            <span className="rounded-full bg-barely-purple px-3 py-1 text-sm font-semibold capitalize text-ktx-purple">
              {activeView} review
            </span>
          )}
        </div>
        {!isViewResolved ? (
          <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 rounded-xl bg-white shadow-knowtex-shadow">
            <div className="h-10 w-10 rounded-full border-4 border-ktx-purple/20 border-t-ktx-purple animate-spin" />
            <p className="text-sm font-medium text-ktx-navy/80">
              Loading your review workspace...
            </p>
          </div>
        ) : !hasValidView ? (
          <div className="rounded-xl bg-white p-6 shadow-knowtex-shadow">
            <p className="font-semibold text-ktx-navy">
              No review role is assigned to your account.
            </p>
            <p className="mt-1 text-sm text-ktx-navy/80">
              Ask your Knowtex contact to set your role to documentation or coding.
            </p>
          </div>
        ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[320px_minmax(0,1fr)] gap-4">
          <VAEvalCaseQuery
            key={activeView}
            view={isKnowtexUser ? activeView : ""}
            reviewedCaseMap={reviewedCaseMap}
            onToggleReviewedCase={toggleReviewedCase}
            onCaseLoaded={handleCaseLoaded}
            onNoCases={handleNoCases}
            onErrorMessageChange={setErrorMessage}
          />

          <div className="relative flex min-h-[62vh] flex-col gap-4 xl:min-h-[82vh]">
            {caseJobs.length > 0 && (
              <div className="mb-4 flex w-fit items-center self-center gap-3 rounded-xl border-2 border-ktx-purple/30 bg-barely-purple px-3 py-2 shadow-md xl:absolute xl:-top-[78px] xl:left-1/2 xl:z-10 xl:mb-0 xl:-translate-x-1/2">
              <button
                type="button"
                onClick={() => cyclePlaybackJob(-1)}
                disabled={caseJobs.length <= 1}
                className="rounded-xl bg-ktx-purple px-5 py-3 text-base font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-ktx-navy hover:shadow-xl disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Previous recording"
              >
                ← Prev
              </button>
              <span className="min-w-[138px] text-center text-base font-extrabold text-ktx-purple">
                Recording {activeJobIndex + 1} of {caseJobs.length}
              </span>
              <button
                type="button"
                onClick={() => cyclePlaybackJob(1)}
                disabled={caseJobs.length <= 1}
                className="rounded-xl bg-ktx-purple px-5 py-3 text-base font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-ktx-navy hover:shadow-xl disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Next recording"
              >
                Next →
              </button>
            </div>
          )}
        <div className="bg-white shadow-knowtex-shadow rounded-xl p-2 shrink-0">
          <div className="mb-1">
            <div className="min-w-0">
              <p className="text-ktx-navy font-semibold">
                {currentAudio.title ? currentAudio.title : "No job loaded"}
              </p>
              {recordingDateTimeLabel && (
                <p className="mt-0.5 text-xs font-medium text-ktx-navy/70">
                  Recorded {recordingDateTimeLabel}
                </p>
              )}
            </div>
          </div>
          <audio
            src={currentAudio.url}
            ref={audioElem}
            onTimeUpdate={onPlaying}
            onLoadedMetadata={onPlaying}
            onEnded={() => setIsPlaying(false)}
          />
          <div className="rounded-xl bg-barely-purple/40 p-2">
            <p className="mb-0.5 text-center text-sm font-medium text-ktx-navy/80">
              {formatTime(currTime)} / {formatTime(duration)}
            </p>
            <input
              type="range"
              min={0}
              max={duration || 0}
              step="0.1"
              value={currTime || 0}
              onChange={handleSeek}
              disabled={!hasAudio}
              style={{ accentColor: "#783bff" }}
              className="w-full disabled:opacity-50"
            />
            <div className="mt-3 flex flex-col gap-3 md:grid md:grid-cols-[140px_1fr_140px] md:items-center">
              <div className="hidden md:block" />
              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => skipBy(-5)}
                  disabled={!hasAudio}
                  className="h-10 min-w-[56px] rounded-md bg-white text-ktx-purple font-semibold shadow-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  -5
                </button>
                <button
                  type="button"
                  onClick={togglePlayback}
                  disabled={!hasAudio}
                  className="h-12 w-12 rounded-full bg-ktx-purple text-white flex items-center justify-center shadow-sm hover:bg-ktx-navy disabled:opacity-50"
                >
                  {isPlaying ? (
                    <svg
                      className="w-5 h-5"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M8 5h3v14H8V5Zm5 0h3v14h-3V5Z" />
                    </svg>
                  ) : (
                    <svg
                      className="w-5 h-5 ml-0.5"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M8 5v14l11-7L8 5Z" />
                    </svg>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => skipBy(5)}
                  disabled={!hasAudio}
                  className="h-10 min-w-[56px] rounded-md bg-white text-ktx-purple font-semibold shadow-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  +5
                </button>
              </div>
              <div className="flex items-center justify-center md:justify-end gap-2 text-sm font-medium text-ktx-navy">
                <span>Speed</span>
                <select
                  value={playbackRate}
                  onChange={handlePlaybackRateChange}
                  disabled={!hasAudio}
                  className="h-10 rounded-md border-none bg-white px-3 text-ktx-purple shadow-sm disabled:opacity-50"
                >
                  <option value={0.5}>0.5x</option>
                  <option value={0.75}>0.75x</option>
                  <option value={1}>1x</option>
                  <option value={1.25}>1.25x</option>
                  <option value={1.5}>1.5x</option>
                  <option value={2}>2x</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        {errorMessage && (
          <p className="text-red-600 font-medium">{errorMessage}</p>
        )}

        <div className="flex flex-1 min-h-[320px] flex-col xl:flex-row gap-4">
          <div className="bg-white shadow-knowtex-shadow rounded-xl p-4 flex flex-1 min-h-0 flex-col">
            <div className="flex items-center justify-between gap-3 px-4 py-3 text-white bg-dark-purple rounded-xl">
              <h2 className="text-[20px]">Transcript</h2>
              {showDocumentation && (
                <button
                  type="button"
                  onClick={() => {
                    void copyTextContent("transcript", transcriptText);
                  }}
                  disabled={!String(transcriptText || "").trim()}
                  className="rounded-md bg-white px-3 py-1 text-sm font-semibold text-ktx-purple hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {copiedField === "transcript" ? "Copied" : "Copy"}
                </button>
              )}
            </div>
            <textarea
              value={transcriptText}
              readOnly
              className="w-full flex-1 h-full min-h-[220px] sm:min-h-[260px] xl:min-h-0 mt-3 p-4 resize-none rounded-xl text-ktx-navy bg-barely-purple border-none focus:outline-none"
            />
          </div>
          <div className="bg-white shadow-knowtex-shadow rounded-xl p-4 flex flex-1 min-h-0 flex-col">
            <div className="flex items-center justify-between gap-3 px-4 py-3 text-white bg-dark-purple rounded-xl">
              <h2 className="text-[20px]">AI Note</h2>
              {showDocumentation && (
                <button
                  type="button"
                  onClick={() => {
                    void copyTextContent("note", aiNoteText);
                  }}
                  disabled={!String(aiNoteText || "").trim()}
                  className="rounded-md bg-white px-3 py-1 text-sm font-semibold text-ktx-purple hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {copiedField === "note" ? "Copied" : "Copy"}
                </button>
              )}
            </div>
            <textarea
              value={aiNoteText}
              readOnly
              className="w-full flex-1 h-full min-h-[220px] sm:min-h-[260px] xl:min-h-0 mt-3 p-4 resize-none rounded-xl text-ktx-navy bg-barely-purple border-none focus:outline-none"
            />
          </div>
        </div>

        {showCoding && codingData && <CodingPanel coding={codingData} />}
        {showCoding && activeCasePatientId && !codingData && (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white p-4 text-sm text-ktx-navy/70">
            This case has no coding snapshot.
          </div>
        )}

        <div
          className={
            hasPrechartHistory && showDocumentation
              ? "grid grid-cols-1 gap-4 xl:grid-cols-2"
              : ""
          }
        >
          {showDocumentation && (
            <LiveTranscriptPanel
              value={liveTranscriptText}
              onChange={handleLiveTranscriptTextChange}
              onCopy={() => {
                void copyTextContent("live_transcript", liveTranscriptText);
              }}
              copied={copiedField === "live_transcript"}
              disabled={!activeCasePatientId}
              readOnly={!canRecordResults}
            />
          )}
          {hasPrechartHistory && (
            <div className="flex h-[280px] min-h-0 flex-col rounded-xl bg-white p-4 shadow-knowtex-shadow">
              <div className="rounded-xl bg-dark-purple px-4 py-3 text-white">
                <h2 className="text-[20px]">CHAPI Data</h2>
              </div>
              <div
                className="mt-2 flex gap-2 overflow-x-auto pb-1"
                aria-label="CHAPI data sections"
              >
                {Object.keys(prechartHistory).map((field) => (
                  <button
                    key={field}
                    type="button"
                    onClick={() => scrollToChapiField(field)}
                    className="shrink-0 rounded-full border border-ktx-purple bg-white px-3 py-1 text-xs font-semibold text-ktx-purple transition hover:bg-ktx-purple hover:text-white"
                  >
                    {formatChartHistoryFieldLabel(field)}
                  </button>
                ))}
              </div>
              <div
                ref={chapiDataScrollRef}
                className="mt-2 min-h-0 flex-1 overflow-auto rounded-xl bg-barely-purple p-4"
              >
                <ChartHistoryTable chartHistory={prechartHistory} />
              </div>
            </div>
          )}
        </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
